/**
 * useContextMenuActions - Handles context menu actions for the automation flow canvas.
 *
 * Extracts the delete and duplicate handlers from the main canvas component
 * to reduce complexity and improve testability.
 */

import type * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import type { BaseNodeData } from '../types';
import type { HistoryActionType } from './useUndoRedo';
import { deleteNodes, type DeletedStepInfo } from '../utils/nodeDelete';
import { getDescendantNodeIds, moveNodeAfter } from '../utils/nodeReordering';
import { isAutomationStepNodeData } from '../types/typeGuards';
import { NODE_TYPES } from '../types';
import type { SegmentNodeData } from '../types/stepNodes';

interface UseContextMenuActionsParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	copySelection: (nodeIds: string[]) => void;
	pasteAtPosition: (position: { x: number; y: number }) => { nodes: Node<BaseNodeData>[]; edges: Edge[] } | null;
	/** Move a step up by swapping with its predecessor */
	moveUp?: (nodeId: string) => boolean;
	/** Move a step down by swapping with its successor */
	moveDown?: (nodeId: string) => boolean;
	/** Callback to persist step deletions to the API */
	onDeleteSteps?: (steps: DeletedStepInfo[]) => Promise<void>;
	/** Callback to persist segment (switch case) deletions to the API */
	onDeleteSegmentCases?: (cases: { switchNodeId: string; caseId: string }[]) => Promise<void>;
	/** Callback to persist a duplication to the API */
	onDuplicateStep?: (params: {
		templateId: string;
		stepType: Api.AutomationStepType;
		step: Api.IAutomationStep;
		beforeId?: string;
	}) => Promise<void>;
}

interface ContextMenuActions {
	handleDelete: (nodeId: string) => void;
	handleDuplicate: (nodeId: string) => void;
	handleMoveUp: (nodeId: string) => void;
	handleMoveDown: (nodeId: string) => void;
}

export function useContextMenuActions({
	nodes,
	edges,
	setNodes,
	setEdges,
	takeSnapshot,
	copySelection,
	pasteAtPosition,
	moveUp,
	moveDown,
	onDeleteSteps,
	onDeleteSegmentCases,
	onDuplicateStep,
}: UseContextMenuActionsParams): ContextMenuActions {
	const handleDelete = useCallback(
		(nodeId: string) => {
			const nodeToDelete = nodes.find((n: Node<BaseNodeData>) => n.id === nodeId);
			if (!nodeToDelete) {
				return;
			}

			const segmentData = nodeToDelete.type === NODE_TYPES.SEGMENT ? (nodeToDelete.data as SegmentNodeData) : null;

			// If we're deleting a segment, we prefer persisting via the switch-case delete endpoint.
			// In that scenario we should NOT also issue per-step delete calls for the branch steps.
			const skipStepDeletionNodeIds = new Set<string>();
			const segmentCasesToDelete: { switchNodeId: string; caseId: string }[] = [];

			if (segmentData && !segmentData.isDefault) {
				segmentCasesToDelete.push({ switchNodeId: segmentData.parentSwitchNodeId, caseId: segmentData.caseId });

				// Always skip steps in the segment's subtree.
				skipStepDeletionNodeIds.add(nodeToDelete.id);
				for (const descendantId of getDescendantNodeIds(nodeToDelete.id, edges)) {
					skipStepDeletionNodeIds.add(descendantId);
				}

				// If deleting the last non-default segment, the UI collapses the switch by also deleting the
				// default segment and the hidden switch node. Skip their step deletions as well.
				const siblingSegments = nodes.filter(
					n =>
						n.type === NODE_TYPES.SEGMENT &&
						(n.data as SegmentNodeData).parentSwitchNodeId === segmentData.parentSwitchNodeId
				) as Node<BaseNodeData>[];

				const nonDefaultSiblingCount = siblingSegments.filter(s => !(s.data as SegmentNodeData).isDefault).length;
				if (nonDefaultSiblingCount === 1) {
					skipStepDeletionNodeIds.add(segmentData.parentSwitchNodeId);

					const defaultSegment = siblingSegments.find(s => (s.data as SegmentNodeData).isDefault);
					if (defaultSegment) {
						skipStepDeletionNodeIds.add(defaultSegment.id);
						for (const descendantId of getDescendantNodeIds(defaultSegment.id, edges)) {
							skipStepDeletionNodeIds.add(descendantId);
						}
					}
				}
			}

			const result = deleteNodes([nodeId], nodes, edges);
			if (result.deletedCount > 0) {
				takeSnapshot('node_delete', `Delete ${nodeToDelete.data.label || 'node'}`);
				setNodes(result.nodes);
				setEdges(result.edges);

				// Persist segment case deletions to the API.
				if (onDeleteSegmentCases && segmentCasesToDelete.length > 0) {
					onDeleteSegmentCases(segmentCasesToDelete).catch(() => {
						toast.error('Failed to persist segment deletion to server');
					});
				}

				// Persist step deletions to the API, excluding any steps that were removed as part of a
				// segment case deletion (those are handled by the switch-case delete endpoint).
				if (onDeleteSteps && result.deletedSteps.length > 0) {
					const stepsToPersist = result.deletedSteps.filter(s => !skipStepDeletionNodeIds.has(s.nodeId || ''));
					if (stepsToPersist.length > 0) {
						onDeleteSteps(stepsToPersist).catch(() => {
							toast.error('Failed to persist step deletion to server');
						});
					}
				}
			}

			if (result.protectedNodeIds.length > 0) {
				toast.info('This node cannot be deleted (triggers and default segments are protected)');
			}
		},
		[nodes, edges, setNodes, setEdges, takeSnapshot, onDeleteSteps, onDeleteSegmentCases]
	);

	const handleDuplicate = useCallback(
		(nodeId: string) => {
			const nodeToDuplicate = nodes.find((n: Node<BaseNodeData>) => n.id === nodeId);
			if (!nodeToDuplicate) {
				return;
			}

			// Optimistically duplicate locally and insert directly after the node.
			copySelection([nodeId]);

			const nodeHeight = nodeToDuplicate.measured?.height || 100;
			const pastePosition = {
				x: nodeToDuplicate.position.x,
				y: nodeToDuplicate.position.y + nodeHeight + 100,
			};

			const pasteResult = pasteAtPosition(pastePosition);
			if (!pasteResult || pasteResult.nodes.length !== 1) {
				return;
			}

			takeSnapshot('paste', `Duplicate ${nodeToDuplicate.data.label || 'node'}`);

			const newNode = pasteResult.nodes[0];
			const nodesWithNew = [...nodes, newNode];
			const edgesWithNew = [...edges, ...pasteResult.edges];

			const moved = moveNodeAfter(nodesWithNew, edgesWithNew, newNode.id, nodeId);
			if (moved.success) {
				setNodes(moved.nodes.map(n => ({ ...n, selected: n.id === newNode.id })));
				setEdges(moved.edges);
				toast.success('Node duplicated');
			}

			// Persist duplication to the API (only supported for saved step nodes).
			if (onDuplicateStep && isAutomationStepNodeData(nodeToDuplicate.data)) {
				const stepId = nodeToDuplicate.data.stepId;
				const templateId = nodeToDuplicate.data.automationTemplate?.id;
				const stepType = nodeToDuplicate.data.stepType;
				const step = nodeToDuplicate.data.automationStep;
				// Determine the successor stepId in this template so we can insert "directly below".
				const successorNode = edges.find(edge => edge.source === nodeId)?.target;
				const successorStepId = successorNode
					? (() => {
							const successor = nodes.find(n => n.id === successorNode);
							if (!successor || !isAutomationStepNodeData(successor.data)) {
								return undefined;
							}
							return successor.data.stepId;
						})()
					: undefined;

				if (stepId && templateId && step) {
					onDuplicateStep({ templateId, stepType, step, beforeId: successorStepId }).catch(() => {
						toast.error('Failed to duplicate step on server');
					});
				}
			}
		},
		[nodes, edges, copySelection, pasteAtPosition, takeSnapshot, setNodes, setEdges, onDuplicateStep]
	);

	const handleMoveUp = useCallback(
		(nodeId: string) => {
			if (moveUp) {
				moveUp(nodeId);
			}
		},
		[moveUp]
	);

	const handleMoveDown = useCallback(
		(nodeId: string) => {
			if (moveDown) {
				moveDown(nodeId);
			}
		},
		[moveDown]
	);

	return {
		handleDelete,
		handleDuplicate,
		handleMoveUp,
		handleMoveDown,
	};
}
